import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import dayjs from "dayjs";
import { z } from "zod";
import { ArrowLeft, CalendarPlus } from "lucide-react";
import { checkUserHasRole } from "../../../utils/auth-utils";
import { ROLES } from "../../../config/roles";
import { Card } from "../../../components/ui/card";
import { Avatar, AvatarFallback } from "../../../components/ui/avatar";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Separator } from "../../../components/ui/separator";
import { Skeleton } from "../../../components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { patientBasicInfoType } from "./PatientTypes";
import { PatientMedicalInfoSchema } from "../MedicalProfile/PatientMedicalProfileForm";

/**
 * Kanban lane the patient currently sits in. This is NOT fetched on the detail
 * page today (it lives on the appointment entity), so the parent passes a
 * best-effort value and the badge only renders when a status is known.
 */
export type PatientLaneStatus = "HAS_APPOINTMENT" | "IN_PROGRESS" | "DONE";

const LANE_META: Record<
  PatientLaneStatus,
  { label: string; variant: "default" | "secondary" | "outline" }
> = {
  HAS_APPOINTMENT: { label: "Has Appointment", variant: "secondary" },
  IN_PROGRESS: { label: "In Progress", variant: "default" },
  DONE: { label: "Done", variant: "outline" },
};

const GENDER_LABEL: Record<string, string> = {
  MALE: "Male",
  FEMALE: "Female",
  OTHER: "Other",
  UNK: "Unknown",
};

function getInitials(firstName?: string | null, lastName?: string | null) {
  const first = firstName?.trim()?.[0] ?? "";
  const last = lastName?.trim()?.[0] ?? "";
  const initials = `${first}${last}`.toUpperCase();
  return initials || "?";
}

export function PatientHeader({
  patientBasicInfoState,
  patientMedicalInfoState,
  status,
}: {
  patientBasicInfoState: patientBasicInfoType;
  patientMedicalInfoState: z.infer<typeof PatientMedicalInfoSchema>;
  status?: PatientLaneStatus;
}) {
  const navigate = useNavigate();
  const { roles } = useAuth();

  // Doctors land on their own board; everyone else uses the shared board.
  const isDoctor = checkUserHasRole([ROLES.DOCTOR_APPOINTMENT_VIEW_ROLE], roles);
  const boardPath = isDoctor ? "/doctor-appointments" : "/appointments";

  // Loading skeleton mirrors the forms' own loading gate (id == null).
  if (patientBasicInfoState.id === null) {
    return (
      <Card className="sticky top-0 z-20 mb-6 rounded-xl border bg-card/95 p-4 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="flex items-center gap-4">
          <Skeleton className="h-14 w-14 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-9 w-48" />
        </div>
      </Card>
    );
  }

  const fullName =
    [patientBasicInfoState.firstName, patientBasicInfoState.lastName]
      .filter(Boolean)
      .join(" ") || "Unknown Patient";

  const dob = patientMedicalInfoState.dateOfBirth;
  const age =
    dob && dayjs(dob).isValid() ? dayjs().diff(dayjs(dob), "year") : null;

  const gender = patientMedicalInfoState.gender
    ? GENDER_LABEL[patientMedicalInfoState.gender] ??
      patientMedicalInfoState.gender
    : null;

  const lane = status ? LANE_META[status] : null;

  // Compact "32 yrs • Male" style line, skipping pieces we don't have.
  const demographics = [age != null ? `${age} yrs` : null, gender]
    .filter(Boolean)
    .join(" • ");

  return (
    <Card className="sticky top-0 z-20 mb-6 rounded-xl border bg-card/95 p-4 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        {/* Identity */}
        <div className="flex items-center gap-4">
          <Avatar className="h-14 w-14 border">
            <AvatarFallback className="bg-primary/10 text-lg font-semibold text-primary">
              {getInitials(
                patientBasicInfoState.firstName,
                patientBasicInfoState.lastName
              )}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-xl font-semibold tracking-tight text-foreground">
                {fullName}
              </h2>
              {lane && <Badge variant={lane.variant}>{lane.label}</Badge>}
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              {demographics && <span>{demographics}</span>}
              {demographics && patientBasicInfoState.id && (
                <Separator orientation="vertical" className="h-3" />
              )}
              {patientBasicInfoState.id && (
                <span className="font-mono text-xs">
                  ID: {patientBasicInfoState.id}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <TooltipProvider delayDuration={150}>
          <div className="flex items-center gap-2 self-start md:self-auto">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/addpatient")}
                >
                  <CalendarPlus className="h-4 w-4" />
                  <span className="hidden sm:inline">Schedule</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Schedule an appointment</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(boardPath)}
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">Back to Board</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Return to the appointments board</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>
    </Card>
  );
}
